import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { NoteCard } from "@/pages/subs/notes/note-card";
import { Note, useNotes } from "@/providers/note-provider";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Plus, FileText } from "lucide-react";
import { useTranslation } from "@/providers/language-provider";

const localeText = {
  en: {
    EmptyHome: "No notes yet",
    EmptyHomeDesc: "Create your first note to get started",
    EmptyFolder: "This note is empty",
    EmptyFolderDesc: "Add sub-notes to organize your content",
    CreateNote: "Create Note",
  },
  ko: {
    EmptyHome: "노트가 없습니다",
    EmptyHomeDesc: "첫 번째 노트를 만들어 시작하세요",
    EmptyFolder: "하위 노트가 없습니다",
    EmptyFolderDesc: "하위 노트를 추가하여 내용을 정리하세요",
    CreateNote: "노트 만들기",
  },
} as const;

export const NoteList = () => {
  const { language } = useTranslation();
  const {
    notes: allNotes,
    rootNotes,
    activeParent,
    updateNote,
    deleteNote,
    selectNote,
    selectParent,
    createNewNote,
  } = useNotes();
  const [notes, setNotes] = useState<Note[]>([]);
  const isChanged = useRef(false);

  const text =
    localeText[language as keyof typeof localeText] ?? localeText["en"];

  const notesRef = useRef<Note[]>(notes);
  const activeParentRef = useRef(activeParent);

  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);
  useEffect(() => {
    activeParentRef.current = activeParent;
  }, [activeParent]);

  useEffect(() => {
    if (activeParent) {
      const filterdNotes = activeParent.children
        ?.map((childId: string) =>
          allNotes.find((note: Note) => note._id === childId),
        )
        .filter(Boolean) as Note[];
      setNotes(filterdNotes || []);
    } else {
      setNotes(rootNotes || []);
    }
  }, [allNotes, rootNotes, activeParent]);

  const updateServer = useCallback(async () => {
    try {
      if (notes?.length > 0 && activeParent) {
        const children = notes.map((note: Note) => note._id);
        console.log("[NoteList] update order of notes", children);
        await updateNote(activeParent, { children });
      }
    } catch (error) {
      console.error(error);
    }
  }, [notes, activeParent, updateNote]);

  useEffect(() => {
    if (isChanged.current) {
      updateServer();
      isChanged.current = false;
    }
  }, [notes, updateServer]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px 이상 움직여야 드래그 시작
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: any) => {
    if (!activeParent) return;

    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      setNotes((items: Note[]) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      isChanged.current = true;
    }
  };

  const handleNavigate = (note: Note) => {
    selectParent(note);
  };

  function createNote() {
    if (activeParent) {
      createNewNote(activeParent._id);
    } else {
      createNewNote();
    }
  }

  //@ts-ignore
  function goToParent() {
    if (activeParent) {
      selectParent(activeParent.parent);
    }
  }

  const isHome = !activeParent;

  if (notes.length === 0) {
    return (
      <div className="w-full h-full flex flex-col justify-center items-center gap-4 p-8">
        <div className="rounded-full bg-muted p-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="text-center">
          <h3 className="font-semibold text-lg">
            {isHome ? text.EmptyHome : text.EmptyFolder}
          </h3>
          <p className="text-muted-foreground text-sm mt-1">
            {isHome ? text.EmptyHomeDesc : text.EmptyFolderDesc}
          </p>
        </div>
        <Button onClick={createNote} className="mt-2">
          <Plus className="h-4 w-4 mr-2" />
          {text.CreateNote}
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto p-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={notes.map((note: Note) => note._id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {/* {activeParent && (
              <button
                onClick={goToParent}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 hover:bg-muted/50 transition-colors min-h-[120px]"
              >
                <MoveUp className="h-6 w-6 text-muted-foreground" />
              </button>
            )} */}
            {notes.map((note: Note) => (
              <NoteCard
                key={note._id}
                note={note}
                onEdit={selectNote}
                onNavigate={handleNavigate}
                onDelete={deleteNote}
              />
            ))}
            <button
              onClick={createNote}
              className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 hover:bg-muted/50 transition-colors min-h-[120px]"
            >
              <Plus className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {text.CreateNote}
              </span>
            </button>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
